import { supabase } from '../../config/supabase';

/**
 * Create a new company
 * @param {Object} companyData - The company data from the form
 * @param {string} createdByUserId - The ID of the user creating the company
 * @param {string} createdByName - The name of the user creating the company
 * @returns {Object} - The created company and success message
 */
export const createCompany = async (companyData, createdByUserId, createdByName) => {
    try {
        // 1. Insert company record
        const { data: company, error: companyError } = await supabase
            .from('companies')
            .insert({
                name: companyData.name,
                slug: companyData.slug,
                logo_url: companyData.logo_url || null,
                primary_color: companyData.primary_color || '#3b82f6',
                secondary_color: companyData.secondary_color || '#8b5cf6',
                contact_email: companyData.contact_email,
                contact_phone: companyData.contact_phone || null,
                address: companyData.address || null,
                city: companyData.city || null,
                state: companyData.state || null,
                postal_code: companyData.postal_code || null,
                country: companyData.country || 'Australia',
                timezone: companyData.timezone || 'Australia/Melbourne',
                is_active: companyData.is_active !== undefined ? companyData.is_active : true,

                // Subscription & Billing
                subscription_plan: companyData.subscription_plan || 'free',
                subscription_status: companyData.subscription_status || 'trial',
                billing_frequency: companyData.billing_frequency || 'monthly',
                pricing_model: companyData.pricing_model || 'fixed',
                base_price: parseFloat(companyData.base_price) || 0,
                per_user_price: parseFloat(companyData.per_user_price) || 0,
                setup_fee: parseFloat(companyData.setup_fee) || 0,
                currency: companyData.currency || 'AUD',

                // Limits
                max_users: companyData.max_users ? parseInt(companyData.max_users) : null,
                max_jobs: companyData.max_jobs ? parseInt(companyData.max_jobs) : null,
                max_storage_gb: companyData.max_storage_gb ? parseInt(companyData.max_storage_gb) : null,

                // Features
                features_included: companyData.features_included || {
                    job_management: true,
                    user_management: true,
                    notifications: true,
                    file_storage: true,
                    reporting: false,
                    custom_branding: false,
                    api_access: false,
                    integrations: false,
                    priority_support: false,
                    advanced_analytics: false
                },

                // Billing Info
                billing_email: companyData.billing_email || null,

                // Trial & Subscription Dates
                trial_start_date: companyData.trial_start_date || null,
                trial_end_date: companyData.trial_end_date || null,

                // Custom Plan
                is_custom_plan: companyData.is_custom_plan || false,
                custom_plan_description: companyData.custom_plan_description || null,
                custom_pricing_notes: companyData.custom_pricing_notes || null,
                contract_terms: companyData.contract_terms || null,

                // Discount
                discount_percentage: parseFloat(companyData.discount_percentage) || 0,
                discount_amount: parseFloat(companyData.discount_amount) || 0,

                // Account Management
                requires_approval: companyData.requires_approval || false,
                account_manager_notes: companyData.account_manager_notes || null,

                // Default settings
                settings: {}
            })
            .select()
            .single();

        if (companyError) throw companyError;

        console.log('Company created successfully:', company);

        return { company, message: 'Company created successfully' };
    } catch (error) {
        console.error('Error creating company:', error);
        throw error;
    }
};

/**
 * Update an existing company
 * @param {string} companyId - The ID of the company to update
 * @param {Object} companyData - The updated company data
 * @returns {Object} - The updated company and success message
 */
export const updateCompany = async (companyId, companyData) => {
    try {
        const { data: company, error: companyError } = await supabase
            .from('companies')
            .update({
                name: companyData.name,
                slug: companyData.slug,
                logo_url: companyData.logo_url || null,
                primary_color: companyData.primary_color,
                secondary_color: companyData.secondary_color,
                contact_email: companyData.contact_email,
                contact_phone: companyData.contact_phone || null,
                address: companyData.address || null,
                city: companyData.city || null,
                state: companyData.state || null,
                postal_code: companyData.postal_code || null,
                country: companyData.country,
                timezone: companyData.timezone,
                is_active: companyData.is_active,

                subscription_plan: companyData.subscription_plan,
                subscription_status: companyData.subscription_status,
                billing_frequency: companyData.billing_frequency,
                pricing_model: companyData.pricing_model,
                base_price: parseFloat(companyData.base_price),
                per_user_price: parseFloat(companyData.per_user_price),
                setup_fee: parseFloat(companyData.setup_fee),
                currency: companyData.currency,

                max_users: companyData.max_users ? parseInt(companyData.max_users) : null,
                max_jobs: companyData.max_jobs ? parseInt(companyData.max_jobs) : null,
                max_storage_gb: companyData.max_storage_gb ? parseInt(companyData.max_storage_gb) : null,

                features_included: companyData.features_included,

                billing_email: companyData.billing_email || null,

                trial_start_date: companyData.trial_start_date || null,
                trial_end_date: companyData.trial_end_date || null,

                is_custom_plan: companyData.is_custom_plan,
                custom_plan_description: companyData.custom_plan_description || null,
                custom_pricing_notes: companyData.custom_pricing_notes || null,
                contract_terms: companyData.contract_terms || null,

                discount_percentage: parseFloat(companyData.discount_percentage),
                discount_amount: parseFloat(companyData.discount_amount),

                requires_approval: companyData.requires_approval,
                account_manager_notes: companyData.account_manager_notes || null,

                updated_at: new Date().toISOString()
            })
            .eq('id', companyId)
            .select()
            .single();

        if (companyError) throw companyError;

        return { company, message: 'Company updated successfully' };
    } catch (error) {
        console.error('Error updating company:', error);
        throw error;
    }
};

/**
 * Get all companies
 * @returns {Array} - Array of all companies
 */
export const getAllCompanies = async () => {
    try {
        const { data: companies, error } = await supabase
            .from('companies')
            .select('*')
            .neq('slug', 'notify')
            .order('created_at', { ascending: false });

        if (error) throw error;

        return companies;
    } catch (error) {
        console.error('Error fetching companies:', error);
        throw error;
    }
};

/**
 * Get a single company by ID
 * @param {string} companyId - The company ID
 * @returns {Object} - The company object
 */
export const getCompanyById = async (companyId) => {
    try {
        const { data: company, error } = await supabase
            .from('companies')
            .select('*')
            .eq('id', companyId)
            .single();

        if (error) throw error;

        return company;
    } catch (error) {
        console.error('Error fetching company:', error);
        throw error;
    }
};

/**
 * Get a single company by slug
 * @param {string} slug - The company slug
 * @returns {Object} - The company object
 */
export const getCompanyBySlug = async (slug) => {
    try {
        const { data: company, error } = await supabase
            .from('companies')
            .select('*')
            .eq('slug', slug)
            .single();

        if (error) throw error;

        return company;
    } catch (error) {
        console.error('Error fetching company by slug:', error);
        throw error;
    }
};

/**
 * Delete a company
 * @param {string} companyId - The company ID to delete
 * @returns {Object} - Success message
 */
export const deleteCompany = async (companyId) => {
    try {
        const { error } = await supabase
            .from('companies')
            .delete()
            .eq('id', companyId);

        if (error) throw error;

        return { message: 'Company deleted successfully' };
    } catch (error) {
        console.error('Error deleting company:', error);
        throw error;
    }
};

/**
 * Toggle company active status
 * @param {string} companyId - The company ID
 * @param {boolean} isActive - The new active status
 * @returns {Object} - The updated company
 */
export const toggleCompanyStatus = async (companyId, isActive) => {
    try {
        const { data: company, error } = await supabase
            .from('companies')
            .update({
                is_active: isActive,
                updated_at: new Date().toISOString()
            })
            .eq('id', companyId)
            .select()
            .single();

        if (error) throw error;

        return company;
    } catch (error) {
        console.error('Error toggling company status:', error);
        throw error;
    }
};

/**
 * Get user count for a company
 * @param {string} companyId - The company ID
 * @returns {number} - Number of active users
 */
export const getCompanyUserCount = async (companyId) => {
    try {
        const { count, error } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId)
            .eq('status', 'active');

        if (error) throw error;

        return count || 0;
    } catch (error) {
        console.error('Error getting company user count:', error);
        return 0;
    }
};
